import { voidGuid } from "../../shared/constants/constants";
import { extractGuids } from "../../shared/utils/guid";

const normalizeImagesBase64 = (publication = {}) => {
  const singleImage =
    publication?.imagenBase64 ||
    publication?.ImagenBase64 ||
    publication?.imageBase64 ||
    publication?.ImageBase64;
  const multipleImages =
    publication?.imagenesBase64 ||
    publication?.ImagenesBase64 ||
    publication?.imagesBase64 ||
    publication?.ImagesBase64;

  const images = [
    ...(singleImage ? [singleImage] : []),
    ...(Array.isArray(multipleImages) ? multipleImages : []),
  ].filter(Boolean);

  return Array.from(new Set(images));
};

const normalizeImageUrls = (publication = {}) => {
  const imageObjects = Array.isArray(publication?.imagenes)
    ? publication.imagenes
    : Array.isArray(publication?.Imagenes)
      ? publication.Imagenes
      : Array.isArray(publication?.images)
        ? publication.images
        : Array.isArray(publication?.Images)
          ? publication.Images
      : [];
  const directUrls = [
    publication?.imagenUrl,
    publication?.ImagenUrl,
    publication?.imageUrl,
    publication?.ImageUrl,
    ...(Array.isArray(publication?.imagenesUrls)
      ? publication.imagenesUrls
      : []),
    ...(Array.isArray(publication?.ImagenesUrls)
      ? publication.ImagenesUrls
      : []),
    ...(Array.isArray(publication?.imagenesUrl)
      ? publication.imagenesUrl
      : []),
    ...(Array.isArray(publication?.ImagenesUrl)
      ? publication.ImagenesUrl
      : []),
    ...(Array.isArray(publication?.imageUrls)
      ? publication.imageUrls
      : []),
    ...(Array.isArray(publication?.ImageUrls)
      ? publication.ImageUrls
      : []),
  ].filter(Boolean);
  const urls = imageObjects
    .map((image) =>
      typeof image === "string"
        ? image
        : image?.url ?? image?.Url ?? image?.URL ?? "",
    )
    .filter(Boolean);

  return Array.from(new Set([...directUrls, ...urls]));
};

const normalizeImageIds = (publication = {}) => {
  return extractGuids(
    publication?.imagen,
    publication?.Imagen,
    publication?.imagenes,
    publication?.Imagenes,
    publication?.images,
    publication?.Images,
  ).filter((id) => id.toLowerCase() !== voidGuid.toLowerCase());
};

const normalizeBooleanFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value ?? "").toLowerCase() === "true";

const normalizeReactors = (reactions) => {
  if (!Array.isArray(reactions)) return [];

  return reactions
    .map((reactor) => ({
      nickname: reactor?.nickname || reactor?.NickName || "",
      idUsuario: reactor?.idusuario || reactor?.idUsuario || reactor?.IdUsuario || "",
    }))
    .filter((reactor) => reactor.nickname || reactor.idUsuario);
};

const normalizeAuthor = (publication = {}) => {
  const idUsuario =
    publication?.idUsuario ??
    publication?.IdUsuario ??
    publication?.idUser ??
    publication?.IdUser ??
    "";
  const nombre =
    publication?.nombrePerfil ??
    publication?.NombrePerfil ??
    publication?.nombre ??
    publication?.Nombre ??
    publication?.nombres ??
    publication?.Nombres ??
    "";
  const apellidos =
    publication?.apellidos ?? publication?.Apellidos ?? "";
  const nickName =
    publication?.nickName ??
    publication?.NickName ??
    publication?.nickname ??
    publication?.Nickname ??
    "";
  const fotoPerfilUrl =
    publication?.fotoPerfilUrl ??
    publication?.FotoPerfilUrl ??
    publication?.profilePic ??
    publication?.ProfilePic ??
    "";

  return {
    idUsuario,
    nombre,
    nombres: publication?.nombres ?? publication?.Nombres ?? nombre,
    apellidos,
    nickName,
    fotoPerfilUrl,
    profilePic: fotoPerfilUrl,
  };
};

const normalizePublicationList = (publications = []) => {
  if (Array.isArray(publications)) return publications;
  if (Array.isArray(publications?.data)) return publications.data;
  if (publications?.data && typeof publications.data === "object") return [publications.data];
  if (Array.isArray(publications?.Data)) return publications.Data;
  if (publications?.Data && typeof publications.Data === "object") return [publications.Data];
  if (publications && typeof publications === "object") return [publications];
  return [];
};

/**
 * Mapper legacy: SOLO publicaciones (POST).
 * Úsalo para pantallas que sigan consumiendo únicamente publicaciones.
 */
export const createPublicationResponse = (publications = []) => {
  return normalizePublicationList(publications).map((publication) => {
    const imagesBase64 = normalizeImagesBase64(publication);
    const imageUrls = normalizeImageUrls(publication);
    const imageIds = normalizeImageIds(publication);
    const photoGuid = imageIds[0] || null;
    const author = normalizeAuthor(publication);

    return {
      text: publication?.comentario ?? publication?.Comentario ?? "",
      image: photoGuid,
      location: publication?.ubicacion ?? publication?.Ubicacion ?? "",
      reactions: publication?.numReacciones ?? publication?.NumReacciones ?? 0,
      comments: publication?.numComentarios ?? publication?.NumComentarios ?? 0,
      idPublication: publication?.idPublicacion ?? publication?.IdPublicacion,
      idPublicacion: publication?.idPublicacion ?? publication?.IdPublicacion,
      idUser: publication?.idUsuario ?? publication?.IdUsuario,
      idUsuario: publication?.idUsuario ?? publication?.IdUsuario,
      nombrePerfil: publication?.nombrePerfil ?? publication?.NombrePerfil ?? "",
      nombre: author.nombre,
      nombres: author.nombres,
      apellidos: author.apellidos,
      nickName: author.nickName,
      fotoPerfilUrl: author.fotoPerfilUrl,
      profilePic: author.profilePic,
      author,
      reacted: normalizeBooleanFlag(publication?.reaccionMia ?? publication?.ReaccionMia),
      reactors: normalizeReactors(publication?.reacciones ?? publication?.Reacciones ?? publication?.reaciones),
      savedPlace: false,
      savedPub: normalizeBooleanFlag(publication?.guardado ?? publication?.Guardado),
      date: publication?.fechaRegistro ?? publication?.FechaRegistro ?? null,
      dateDetails:
        publication?.fechaCreacion ??
        publication?.FechaCreacion ??
        publication?.fechaRegistro ??
        publication?.FechaRegistro ??
        null,
      imageBase64:
        publication?.imagenBase64 ||
        publication?.ImagenBase64 ||
        publication?.imageBase64 ||
        publication?.ImageBase64 ||
        null,
      imagesBase64,
      imagenesBase64: imagesBase64,
      imageUrls,
      imagenesUrl: imageUrls,
      imageIds,
      mentions: publication?.arrobados ?? publication?.Arrobados ?? [],
    };
  });
};

/**
 * Mapper NUEVO: feed mixto (POST + COUPON) respetando el orden del backend.
 * Este es el que usarás para Home (3 publicaciones : 1 cupón).
 */
export const createMainFeedResponse = (items = []) => {
  return normalizePublicationList(items)
    .map((item) => {
      // COUPON
      if (item?.tipoRegistro === "COUPON" && Number(item?.idCupon) > 0) {
       return {
  feedType: "coupon",
  id: item.idPublicacion,
  coupon: {
    idCupon: item.idCupon,
    title: item.couponTitle,
    discountValue: item.discountValue,
    couponCode: item.couponCode,
    categoryName: item.categoryName,
    endDate: item.endDate,
    discountTypeName: item.discountTypeName,
    saved: normalizeBooleanFlag(item?.guardado),
  },
};
      }

      // POST
      if (item?.tipoRegistro === "POST") {
        const imagesBase64 = normalizeImagesBase64(item);
        const imageUrls = normalizeImageUrls(item);
        const imageIds = normalizeImageIds(item);
        const photoGuid = imageIds[0] || null;
        const author = normalizeAuthor(item);

        return {
          feedType: "publication",
          id: item.idPublicacion,
          publication: {
            text: item?.comentario ?? "",
            image: photoGuid,
            location: item?.ubicacion ?? "",
            reactions: item?.numReacciones || 0,
            comments: item?.numComentarios || 0,
            idPublication: item?.idPublicacion,
            idPublicacion: item?.idPublicacion,
            idUser: item?.idUsuario,
            idUsuario: item?.idUsuario,
            nombrePerfil: item?.nombrePerfil ?? item?.NombrePerfil ?? "",
            nombre: author.nombre,
            nombres: author.nombres,
            apellidos: author.apellidos,
            nickName: author.nickName,
            fotoPerfilUrl: author.fotoPerfilUrl,
            profilePic: author.profilePic,
            author,
            reacted: normalizeBooleanFlag(item?.reaccionMia),
            reactors: normalizeReactors(item?.reacciones ?? item?.reaciones),
            savedPlace: false,
            savedPub: normalizeBooleanFlag(item?.guardado),
            date: item?.fechaRegistro ?? null,
            dateDetails: item?.fechaCreacion ?? item?.fechaRegistro ?? null,
            imageBase64: item?.imagenBase64 || null,
            imagesBase64,
            imagenesBase64: imagesBase64,
            imageUrls,
            imagenesUrl: imageUrls,
            imageIds,
            mentions: item?.arrobados || [],
          },
        };
      }

     
      return null;
    })
    .filter(Boolean);
};

/**
 * Mapper de comentarios (otro endpoint).
 */
export const createCommentsResponse = (comments = []) => {
  return comments.map((comment) => {
    const imageGuid =
      comment?.imagen && comment.imagen !== voidGuid ? comment.imagen : null;

    return {
      text: comment?.comentario ?? "",
      date: comment?.fechaCreacion ?? null,
      idComment: comment?.idComentario,
      reactions: comment?.numReacciones || 0,
      image: imageGuid,
      userId: comment?.idUsuario,
      profilePic: comment?.profilePic ?? null,
      imagenBase64: comment?.imagenBase64 ?? null,
      fotoPerfilBase64: comment?.fotoPerfilBase64 ?? null,
      reaccionMia: Boolean(comment?.reaccionMia),
    };
  });
};
